// Public/dynamic content service — all calls go through geo-api.
// Every content item carries an approval-workflow `publishStatus`. The frontend
// filters anything that is not `published` for public consumers; admin preview
// mode (`?preview=1`) lets the portal render draft/in-review content for
// content managers without exposing it publicly.
//
// No content is hard-coded for production rendering. Typed fallbacks exist only
// so the pilot UI does not collapse when a geo-api endpoint is still pending —
// every fallback item is explicitly marked `published` and clearly labelled as
// pilot content.
import { geoApi } from '@/lib/geoApi';

// ─── Workflow + governance ──────────────────────────────────────────────────
export type PublishStatus =
  | 'draft'
  | 'in_review'
  | 'approved'
  | 'published'
  | 'archived';

export type GovernanceStatus =
  | 'pending_approval'
  | 'approved'
  | 'locked'
  | 'under_revision';

export interface ContentMeta {
  publishStatus: PublishStatus;
  updatedAt?: string;
  approvedBy?: string;
}

// ─── Content types ──────────────────────────────────────────────────────────
export interface HomepageBanner extends ContentMeta {
  id: string;
  title: string;
  subtitle?: string;
  ctaLabel?: string;
  ctaHref?: string;
  imageUrl?: string;
}

export interface PublicProject extends ContentMeta {
  id: string;
  name: string;
  region: string;
  type: string;
  status: 'Open' | 'Due Diligence' | 'Reserved' | 'Closed' | string;
  restricted?: boolean;
}

export interface ServiceItem extends ContentMeta {
  id: string;
  title: string;
  description: string;
  icon?: string;
}

export interface FeatureItem extends ContentMeta {
  id: string;
  title: string;
  description: string;
}

export interface PricingPlan extends ContentMeta {
  id: string;
  name: string;
  price: string;
  cadence: string;
  features: string[];
  highlighted?: boolean;
  governanceStatus?: GovernanceStatus;
}

export interface PublicNotice extends ContentMeta {
  id: string;
  title: string;
  body: string;
  publishedAt: string;
  severity?: 'info' | 'warning' | 'critical';
}

export interface PublicVideo extends ContentMeta {
  id: string;
  title: string;
  url: string;
  thumbnail?: string;
}

export interface Publication extends ContentMeta {
  id: string;
  title: string;
  author?: string;
  year?: number;
  url?: string;
  fileType?: 'PDF' | 'XLS' | 'ZIP' | string;
}

export interface OfficeContact extends ContentMeta {
  id: string;
  name: string;
  address: string;
  phone?: string;
  email?: string;
}

export interface TeamProfile extends ContentMeta {
  id: string;
  name: string;
  role: string;
  bio?: string;
  photoUrl?: string;
}

export interface ExecutiveMessage extends ContentMeta {
  id: string;
  authorName: string;
  authorTitle: string;
  message: string;
  photoUrl?: string;
}

export interface GisShowcaseItem extends ContentMeta {
  id: string;
  title: string;
  description: string;
  region?: string;
  previewImageUrl?: string;
}

export interface EnvironmentalNotice extends ContentMeta {
  id: string;
  title: string;
  body: string;
  category: 'safety' | 'awareness' | 'compliance' | string;
}

// ─── Fallbacks (all explicitly `published`) ─────────────────────────────────
const PUB: ContentMeta = { publishStatus: 'published' };

const FALLBACK_BANNERS: HomepageBanner[] = [
  {
    id: 'b1',
    title: 'Discover, Invest & Explore the Earth',
    subtitle:
      'GIISDEC unifies geological survey data, mineral resource intelligence, and investor opportunities into a single secure portal.',
    ctaLabel: 'Investor Access',
    ctaHref: '/investor-login',
    ...PUB,
  },
];

// P9B: no fabricated concession/project fixture. Invented project names must not
// masquerade as approved government project data. When the server publishes no
// public projects, the UI shows an honest validation-safe state instead.

const FALLBACK_SERVICES: ServiceItem[] = [
  { id: 's1', title: 'Geological Data Access', description: 'Curated survey datasets and concession metadata.', ...PUB },
  { id: 's2', title: 'Investor Onboarding', description: 'KYC, MOU/NDA/LOI and document workflows.', ...PUB },
  // P9B: Permit/Licence administration is deferred from the first pilot (Admin PL0).
  // Public copy describes regulatory INFORMATION, not an operational apply/issue claim.
  { id: 's3', title: 'Regulatory Information', description: 'Guidance on permits, licensing and regulatory procedures.', ...PUB },
  { id: 's4', title: 'Restricted Data Requests', description: 'Request access to governed datasets under the appropriate agreements.', ...PUB },
  { id: 's5', title: 'GIS Analyst Workspace', description: 'Multi-layer map viewer and spatial query tools.', ...PUB },
  { id: 's6', title: 'Advisory & Support', description: 'Direct support channel for investors and analysts.', ...PUB },
];

const FALLBACK_FEATURES: FeatureItem[] = [
  { id: 'f1', title: 'Single Source of Truth', description: 'Unified geological + investment data.', ...PUB },
  { id: 'f2', title: 'Role-Based Access', description: 'Investor, analyst, and admin roles enforced server-side.', ...PUB },
  { id: 'f3', title: 'Authenticated Services', description: 'Every workflow runs through authenticated GIISDEC services.', ...PUB },
  { id: 'f4', title: 'Audit Trail Ready', description: 'Every request/approval logged for compliance.', ...PUB },
  // P9B: payments are not an operational public feature in this pilot — present
  // as a prepared capability, not a live service.
  { id: 'f5', title: 'Finance-Verified Payments', description: 'Invoice payments verified manually by GIISDEC Finance.', ...PUB },
  { id: 'f6', title: 'Built for National Rollout', description: 'Foundation built for enterprise rollout.', ...PUB },
];

// P9B: NOT commercial subscription tiers. These describe ACCESS LEVELS — who can
// use each part of the platform. No prices, cadences, "Enterprise", bulk-export
// or API-sales positioning. `price` carries a short access descriptor, not money.
const FALLBACK_PRICING: PricingPlan[] = [
  { id: 'pp1', name: 'Public', price: 'Open', cadence: '', features: ['Public geological information', 'Project directory', 'Public notices & publications'], governanceStatus: 'approved', ...PUB },
  { id: 'pp2', name: 'Investor', price: 'Register', cadence: '', features: ['Verified investor onboarding (KYC)', 'Secure document workspace', 'Information & service requests'], highlighted: true, governanceStatus: 'approved', ...PUB },
  { id: 'pp3', name: 'Government & Agency', price: 'Institutional', cadence: '', features: ['Access provisioned by GIISDEC', 'Role-based oversight', 'Regulatory & executive information'], governanceStatus: 'approved', ...PUB },
];

// ─── Publish filter helper ──────────────────────────────────────────────────
//
// DEFENSIVE, ALWAYS-ON publication guard (R11P GEOLOGICAL P0.5-P9A §23).
// The client NEVER discloses unpublished content on the strength of a URL flag.
// Earlier this honoured `opts.preview` and returned every row — which let anyone
// append `?preview=1` and imply an authority the client cannot grant. Publication
// authority belongs to the server alone; `preview` is now display-only (it drives
// the admin preview banner, not what content is exposed). Governance invariant:
// GEOLOGICAL APPROVAL ≠ INTERNAL GIS PUBLICATION ≠ PUBLIC DISCLOSURE.
function filterPublished<T extends ContentMeta>(
  rows: T[] | null | undefined,
  opts?: { preview?: boolean }
): T[] {
  if (!rows) return [];
  void opts; // display-only; must not loosen the published filter
  return rows.filter((r) => r.publishStatus === 'published');
}

// Display-only indicator that the admin preview URL flag is set. It changes the
// on-screen banner, NOT which content is disclosed (see filterPublished).
export function isPreviewMode(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return new URLSearchParams(window.location.search).get('preview') === '1';
  } catch {
    return false;
  }
}

// ─── Service surface ────────────────────────────────────────────────────────
export const contentService = {
  isPreviewMode,

  async listBanners(opts?: { preview?: boolean }): Promise<HomepageBanner[]> {
    const res = await geoApi.get<HomepageBanner[]>('/public/banners');
    return filterPublished<HomepageBanner>(res.data ?? FALLBACK_BANNERS, opts);
  },
  async listProjects(opts?: { preview?: boolean }): Promise<PublicProject[]> {
    const res = await geoApi.get<PublicProject[]>('/public/projects');
    // No fabricated project fallback (P9B): absent server data → honest empty state.
    return filterPublished<PublicProject>(res.data ?? [], opts);
  },
  async listServices(opts?: { preview?: boolean }): Promise<ServiceItem[]> {
    const res = await geoApi.get<ServiceItem[]>('/public/services');
    return filterPublished<ServiceItem>(res.data ?? FALLBACK_SERVICES, opts);
  },
  async listFeatures(opts?: { preview?: boolean }): Promise<FeatureItem[]> {
    const res = await geoApi.get<FeatureItem[]>('/public/features');
    return filterPublished<FeatureItem>(res.data ?? FALLBACK_FEATURES, opts);
  },
  async listPricing(opts?: { preview?: boolean }): Promise<PricingPlan[]> {
    const res = await geoApi.get<PricingPlan[]>('/public/pricing');
    return filterPublished<PricingPlan>(res.data ?? FALLBACK_PRICING, opts);
  },
  async listNotices(opts?: { preview?: boolean }): Promise<PublicNotice[]> {
    const res = await geoApi.get<PublicNotice[]>('/public/notices');
    return filterPublished<PublicNotice>(res.data ?? [], opts);
  },
  async listVideos(opts?: { preview?: boolean }): Promise<PublicVideo[]> {
    const res = await geoApi.get<PublicVideo[]>('/public/videos');
    return filterPublished<PublicVideo>(res.data ?? [], opts);
  },
  async listPublications(opts?: { preview?: boolean }): Promise<Publication[]> {
    const res = await geoApi.get<Publication[]>('/public/publications');
    return filterPublished<Publication>(res.data ?? [], opts);
  },
  async listOffices(opts?: { preview?: boolean }): Promise<OfficeContact[]> {
    const res = await geoApi.get<OfficeContact[]>('/public/offices');
    return filterPublished<OfficeContact>(res.data ?? [], opts);
  },
  async listTeam(opts?: { preview?: boolean }): Promise<TeamProfile[]> {
    const res = await geoApi.get<TeamProfile[]>('/public/team');
    return filterPublished<TeamProfile>(res.data ?? [], opts);
  },
  async listExecutiveMessages(opts?: { preview?: boolean }): Promise<ExecutiveMessage[]> {
    const res = await geoApi.get<ExecutiveMessage[]>('/public/executive-messages');
    return filterPublished<ExecutiveMessage>(res.data ?? [], opts);
  },
  async listGisShowcase(opts?: { preview?: boolean }): Promise<GisShowcaseItem[]> {
    const res = await geoApi.get<GisShowcaseItem[]>('/public/gis-showcase');
    return filterPublished<GisShowcaseItem>(res.data ?? [], opts);
  },
  async listEnvironmentalNotices(opts?: { preview?: boolean }): Promise<EnvironmentalNotice[]> {
    const res = await geoApi.get<EnvironmentalNotice[]>('/public/environmental-notices');
    return filterPublished<EnvironmentalNotice>(res.data ?? [], opts);
  },
};
